import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TruckIcon,
  MapIcon,
  TicketIcon,
  DevicePhoneMobileIcon,
  RectangleStackIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { transportAPI } from '../services/api';
import { LoadingOverlay } from '../components/ui/LoadingOverlay';

type TransportTab = 'rental' | 'driving' | 'transit' | 'rideshare' | 'trains';

interface TabConfig {
  id: TransportTab;
  name: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  description: string;
}

const TABS: TabConfig[] = [
  { id: 'rental', name: 'Rental Cars', icon: TruckIcon, description: 'Car rental info, insurance, and tips' },
  { id: 'driving', name: 'Driving Info', icon: MapIcon, description: 'Driving rules, roads, and parking' },
  { id: 'transit', name: 'Public Transit', icon: TicketIcon, description: 'Metro, bus, tram systems and passes' },
  { id: 'rideshare', name: 'Taxis & Rides', icon: DevicePhoneMobileIcon, description: 'Rideshare apps, taxis, and transfers' },
  { id: 'trains', name: 'Trains', icon: RectangleStackIcon, description: 'Rail networks, passes, and scenic routes' },
];

const TRIP_TYPES = ['city', 'road-trip', 'mixed'];
const REGIONS = ['europe', 'asia', 'north-america', 'south-america', 'africa', 'oceania'];

export const GroundTransportGuide: React.FC = () => {
  const { t } = useTranslation(['transport', 'common']);
  const [activeTab, setActiveTab] = useState<TransportTab>('rental');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Common destination for all tabs
  const [destination, setDestination] = useState('');
  const [tripType, setTripType] = useState('');
  const [region, setRegion] = useState('');

  // Results
  const [rentalResult, setRentalResult] = useState<any>(null);
  const [drivingResult, setDrivingResult] = useState<any>(null);
  const [transitResult, setTransitResult] = useState<any>(null);
  const [rideshareResult, setRideshareResult] = useState<any>(null);
  const [trainResult, setTrainResult] = useState<any>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!destination.trim()) return;
    setLoading(true); setError(null);
    try {
      switch (activeTab) {
        case 'rental':
          setRentalResult(await transportAPI.rentalCars({ destination: destination.trim(), trip_type: tripType || undefined }));
          break;
        case 'driving':
          setDrivingResult(await transportAPI.drivingInfo({ destination: destination.trim() }));
          break;
        case 'transit':
          setTransitResult(await transportAPI.publicTransit({ destination: destination.trim() }));
          break;
        case 'rideshare':
          setRideshareResult(await transportAPI.rideshareTaxis({ destination: destination.trim() }));
          break;
        case 'trains':
          setTrainResult(await transportAPI.trains({ destination: destination.trim(), region: region || undefined }));
          break;
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to get transportation info. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderArray = (arr: any[]) => {
    if (!arr || arr.length === 0) return null;
    return <ul className="list-disc list-inside space-y-1">{arr.map((item, i) => <li key={i} className="text-gray-700 dark:text-gray-300">{String(item)}</li>)}</ul>;
  };

  const renderKV = (obj: any) => {
    if (!obj || typeof obj !== 'object') return null;
    return (
      <div className="space-y-2">
        {Object.entries(obj).map(([key, val]: [string, any]) => (
          <p key={key} className="text-gray-700 dark:text-gray-300">
            <strong className="capitalize">{key.replace(/_/g, ' ')}:</strong>{' '}
            {typeof val === 'boolean' ? (val ? 'Yes' : 'No') : typeof val === 'object' ? JSON.stringify(val) : String(val)}
          </p>
        ))}
      </div>
    );
  };

  const inputCls = "w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-cyan-500 focus:border-transparent";
  const btnCls = "flex items-center gap-2 px-6 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors";
  const cardCls = "bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700";

  const getResult = () => {
    switch (activeTab) {
      case 'rental': return rentalResult;
      case 'driving': return drivingResult;
      case 'transit': return transitResult;
      case 'rideshare': return rideshareResult;
      case 'trains': return trainResult;
    }
  };

  const result = getResult();

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <LoadingOverlay isVisible={loading} message="Researching transportation options..." subMessage="Our AI is analyzing local transport info" />

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <TruckIcon className="h-7 w-7 text-cyan-600" />
          {t('transport:title')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">Everything you need to know about getting around</p>
      </div>

      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="flex space-x-4 overflow-x-auto">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} onClick={() => { setActiveTab(tab.id); setError(null); }}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${activeTab === tab.id ? 'border-cyan-500 text-cyan-600 dark:text-cyan-400' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'}`}>
                <Icon className="h-5 w-5" />{tab.name}
              </button>
            );
          })}
        </nav>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
          <span className="text-red-700 dark:text-red-300">{error}</span>
        </div>
      )}

      {/* Common Form */}
      <form onSubmit={handleSubmit} className={cardCls}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className={activeTab === 'rental' || activeTab === 'trains' ? '' : 'md:col-span-3'}>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('transport:labels.destination')}</label>
            <input type="text" value={destination} onChange={(e) => setDestination(e.target.value)} placeholder="e.g., Paris" className={inputCls} />
          </div>
          {activeTab === 'rental' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('transport:labels.tripType')} (optional)</label>
              <select value={tripType} onChange={(e) => setTripType(e.target.value)} className={inputCls}>
                <option value="">Any type</option>
                {TRIP_TYPES.map(tt => <option key={tt} value={tt}>{tt.charAt(0).toUpperCase() + tt.slice(1).replace('-', ' ')}</option>)}
              </select>
            </div>
          )}
          {activeTab === 'trains' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('transport:labels.region')} (optional)</label>
              <select value={region} onChange={(e) => setRegion(e.target.value)} className={inputCls}>
                <option value="">Any region</option>
                {REGIONS.map(r => <option key={r} value={r}>{r.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</option>)}
              </select>
            </div>
          )}
        </div>
        <button type="submit" disabled={loading || !destination.trim()} className={btnCls}>
          <MagnifyingGlassIcon className="h-5 w-5" />Search
        </button>
      </form>

      {/* Results */}
      {result && (
        <div className="mt-6 space-y-4">
          {/* Rental Cars Results */}
          {activeTab === 'rental' && rentalResult && (
            <>
              {rentalResult.recommended_classes && (
                <div className={cardCls}>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('transport:sections.recommendedClasses')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {rentalResult.recommended_classes.map((c: any, i: number) => (
                      <div key={i} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <p className="font-medium text-gray-900 dark:text-white">{c.class}</p>
                        <p className="text-sm text-cyan-600 dark:text-cyan-400">{c.typical_price}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{c.best_for}</p>
                        {c.notes && <p className="text-sm text-gray-500 dark:text-gray-400 italic">{c.notes}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {rentalResult.insurance && <div className={cardCls}><h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('transport:sections.insurance')}</h3>{renderKV(rentalResult.insurance)}</div>}
              {rentalResult.requirements && <div className={cardCls}><h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('transport:sections.requirements')}</h3>{renderKV(rentalResult.requirements)}</div>}
              {rentalResult.fuel && <div className={cardCls}><h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('transport:sections.fuel')}</h3>{renderKV(rentalResult.fuel)}</div>}
              {rentalResult.parking && <div className={cardCls}><h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('transport:sections.parking')}</h3>{renderKV(rentalResult.parking)}</div>}
              {rentalResult.companies && <div className={cardCls}><h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Rental Companies</h3><p className="text-gray-700 dark:text-gray-300">{rentalResult.companies.join(', ')}</p></div>}
              {rentalResult.tips && <div className={cardCls}><h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('transport:sections.tips')}</h3>{renderArray(rentalResult.tips)}</div>}
            </>
          )}

          {/* Driving Info Results */}
          {activeTab === 'driving' && drivingResult && (
            <>
              <div className={cardCls}>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {drivingResult.driving_side && <div className="text-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"><p className="text-sm text-gray-500 dark:text-gray-400">Driving Side</p><p className="text-lg font-bold text-gray-900 dark:text-white">{drivingResult.driving_side}</p></div>}
                </div>
              </div>
              {drivingResult.speed_limits && <div className={cardCls}><h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('transport:sections.speedLimits')}</h3>{renderKV(drivingResult.speed_limits)}</div>}
              {drivingResult.tolls && <div className={cardCls}><h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('transport:sections.tolls')}</h3>{renderKV(drivingResult.tolls)}</div>}
              {drivingResult.fuel && <div className={cardCls}><h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('transport:sections.fuel')}</h3>{renderKV(drivingResult.fuel)}</div>}
              {drivingResult.parking && <div className={cardCls}><h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('transport:sections.parking')}</h3>{renderKV(drivingResult.parking)}</div>}
              {drivingResult.road_conditions && <div className={cardCls}><h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('transport:sections.roadConditions')}</h3>{renderKV(drivingResult.road_conditions)}</div>}
              {drivingResult.rules && <div className={cardCls}><h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('transport:sections.rules')}</h3>{renderKV(drivingResult.rules)}</div>}
              {drivingResult.tips && <div className={cardCls}><h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('transport:sections.tips')}</h3>{renderArray(drivingResult.tips)}</div>}
            </>
          )}

          {/* Public Transit Results */}
          {activeTab === 'transit' && transitResult && (
            <>
              {transitResult.systems && (
                <div className={cardCls}>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('transport:sections.systems')}</h3>
                  <div className="space-y-4">
                    {transitResult.systems.map((s: any, i: number) => (
                      <div key={i} className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="px-2 py-1 bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 rounded text-xs font-medium">{s.type}</span>
                          <h4 className="font-medium text-gray-900 dark:text-white">{s.name}</h4>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                          {s.single_fare && <div><span className="text-gray-500 dark:text-gray-400">Single:</span> <span className="text-gray-700 dark:text-gray-300">{s.single_fare}</span></div>}
                          {s.day_pass && <div><span className="text-gray-500 dark:text-gray-400">Day pass:</span> <span className="text-gray-700 dark:text-gray-300">{s.day_pass}</span></div>}
                          {s.hours && <div><span className="text-gray-500 dark:text-gray-400">Hours:</span> <span className="text-gray-700 dark:text-gray-300">{s.hours}</span></div>}
                          {s.frequency && <div><span className="text-gray-500 dark:text-gray-400">Frequency:</span> <span className="text-gray-700 dark:text-gray-300">{s.frequency}</span></div>}
                        </div>
                        {s.coverage && <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">{s.coverage}</p>}
                        {s.tips && <div className="mt-2">{renderArray(s.tips)}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {transitResult.recommended_passes && (
                <div className={cardCls}>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('transport:sections.passes')}</h3>
                  <div className="space-y-3">
                    {transitResult.recommended_passes.map((p: any, i: number) => (
                      <div key={i} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <p className="font-medium text-gray-900 dark:text-white">{p.name} <span className="text-cyan-600 dark:text-cyan-400">{p.price}</span></p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{p.duration} &mdash; {p.includes}</p>
                        {p.worth_it_if && <p className="text-sm text-gray-500 dark:text-gray-400 italic">{p.worth_it_if}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {transitResult.apps && <div className={cardCls}><h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('transport:sections.apps')}</h3><div className="space-y-2">{transitResult.apps.map((a: any, i: number) => <p key={i} className="text-gray-700 dark:text-gray-300"><strong>{a.name}:</strong> {a.purpose}</p>)}</div></div>}
              {transitResult.airport_connections && <div className={cardCls}><h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('transport:sections.airportConnections')}</h3><p className="text-gray-700 dark:text-gray-300">{transitResult.airport_connections}</p></div>}
              {transitResult.safety_tips && <div className={cardCls}><h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('transport:sections.safetyTips')}</h3>{renderArray(transitResult.safety_tips)}</div>}
              {transitResult.tourist_tips && <div className={cardCls}><h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('transport:sections.touristTips')}</h3>{renderArray(transitResult.tourist_tips)}</div>}
            </>
          )}

          {/* Rideshare & Taxi Results */}
          {activeTab === 'rideshare' && rideshareResult && (
            <>
              {rideshareResult.rideshare_services && (
                <div className={cardCls}>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('transport:sections.rideshareServices')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {rideshareResult.rideshare_services.map((s: any, i: number) => (
                      <div key={i} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-gray-900 dark:text-white">{s.name}</p>
                          <span className={`px-2 py-0.5 rounded text-xs ${s.available ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'}`}>{s.available ? 'Available' : 'Not available'}</span>
                        </div>
                        {s.typical_pricing && <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{s.typical_pricing}</p>}
                        {s.payment && <p className="text-sm text-gray-500 dark:text-gray-400">Payment: {s.payment}</p>}
                        {s.tips && <p className="text-sm text-gray-500 dark:text-gray-400 italic mt-1">{s.tips}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {rideshareResult.traditional_taxis && <div className={cardCls}><h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('transport:sections.traditionalTaxis')}</h3>{renderKV(rideshareResult.traditional_taxis)}</div>}
              {rideshareResult.airport_transfers && <div className={cardCls}><h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('transport:sections.airportTransfers')}</h3>{renderKV(rideshareResult.airport_transfers)}</div>}
              {rideshareResult.scam_warnings && <div className={`${cardCls} bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800`}><h3 className="text-lg font-semibold text-red-800 dark:text-red-300 mb-3">{t('transport:sections.scamWarnings')}</h3>{renderArray(rideshareResult.scam_warnings)}</div>}
              {rideshareResult.safety_tips && <div className={cardCls}><h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('transport:sections.safetyTips')}</h3>{renderArray(rideshareResult.safety_tips)}</div>}
              {rideshareResult.general_tips && <div className={cardCls}><h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('transport:sections.tips')}</h3>{renderArray(rideshareResult.general_tips)}</div>}
            </>
          )}

          {/* Train Results */}
          {activeTab === 'trains' && trainResult && (
            <>
              {trainResult.rail_network && <div className={cardCls}><h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('transport:sections.railNetwork')}</h3>{renderKV(trainResult.rail_network)}</div>}
              {trainResult.popular_routes && (
                <div className={cardCls}>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('transport:sections.popularRoutes')}</h3>
                  <div className="space-y-3">
                    {trainResult.popular_routes.map((r: any, i: number) => (
                      <div key={i} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <p className="font-medium text-gray-900 dark:text-white">{r.route}</p>
                        <div className="flex flex-wrap gap-3 text-sm mt-1">
                          {r.duration && <span className="text-gray-600 dark:text-gray-400">{r.duration}</span>}
                          {r.price_range && <span className="text-cyan-600 dark:text-cyan-400">{r.price_range}</span>}
                          {r.type && <span className="px-2 py-0.5 bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 rounded text-xs">{r.type}</span>}
                          {r.scenic_rating && <span className="text-gray-500 dark:text-gray-400">Scenic: {r.scenic_rating}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {trainResult.rail_passes && (
                <div className={cardCls}>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('transport:sections.railPasses')}</h3>
                  <div className="space-y-3">
                    {trainResult.rail_passes.map((p: any, i: number) => (
                      <div key={i} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <p className="font-medium text-gray-900 dark:text-white">{p.name} <span className="text-cyan-600 dark:text-cyan-400">{p.price_range}</span></p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{p.duration}</p>
                        {p.worth_it_if && <p className="text-sm text-gray-500 dark:text-gray-400 italic">{p.worth_it_if}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {trainResult.scenic_routes && (
                <div className={cardCls}>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('transport:sections.scenicRoutes')}</h3>
                  <div className="space-y-3">
                    {trainResult.scenic_routes.map((r: any, i: number) => (
                      <div key={i} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <p className="font-medium text-gray-900 dark:text-white">{r.name}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{r.description}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{r.duration} &mdash; Best: {r.best_season}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {trainResult.classes && <div className={cardCls}><h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('transport:sections.classes')}</h3>{renderKV(trainResult.classes)}</div>}
              {trainResult.booking_tips && <div className={cardCls}><h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('transport:sections.bookingTips')}</h3>{renderArray(trainResult.booking_tips)}</div>}
              {trainResult.station_tips && <div className={cardCls}><h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('transport:sections.stationTips')}</h3>{renderArray(trainResult.station_tips)}</div>}
            </>
          )}
        </div>
      )}

      <div className="mt-8 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
        <p className="text-xs text-gray-500 dark:text-gray-400">{t('common:disclaimer.travel')}</p>
      </div>
    </div>
  );
};

export default GroundTransportGuide;
